import { useEffect, useState } from "react";
import { CalendarCheck, Check, Download, Lock } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const KIND = { setup: "text-amber-300", prompt: "text-purple-300", execute: "text-zinc-400", review: "text-emerald-300", ship: "text-amber-200" };

export const ThirtyDayPlan = ({ slug, onJumpToPrompt }) => {
  const [plan, setPlan] = useState(null);
  const [checked, setChecked] = useState(new Set());

  useEffect(() => {
    api.get(`/plans/${slug}`).then((r) => { setPlan(r.data); setChecked(new Set(r.data.checked_days)); }).catch(() => setPlan(null));
  }, [slug]);

  if (!plan) return null;

  const toggle = async (day) => {
    if (plan.locked) return;
    const next = new Set(checked);
    next.has(day) ? next.delete(day) : next.add(day);
    setChecked(next);
    trackEvent("plan_day_toggle", { slug, day, done: next.has(day) });
    await api.put(`/plans/${slug}/state`, { checked_days: [...next] }).catch(() => {});
  };

  const exportCsv = () => {
    const rows = [["day", "type", "task", "prompt_number", "done"], ...plan.days.map((d) => [d.day, d.kind, `"${d.title.replace(/"/g, '""')}"`, d.prompt_index != null ? d.prompt_index + 1 : "", checked.has(d.day) ? "yes" : ""])];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `stag-vault-${slug}-30-day-plan.csv`; a.click();
  };

  const pct = Math.round((checked.size / 30) * 100);
  return (
    <section className="mt-10 border border-zinc-800 bg-[#08070b]" data-testid="thirty-day-plan">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 px-6 py-5">
        <div>
          <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-purple-300"><CalendarCheck size={13} /> 30-day plan</div>
          <h2 className="mt-2 font-display text-2xl font-black tracking-tight text-white">Day 1 to day 30. One job a day.</h2>
          <p className="mt-1 text-sm text-zinc-400">Built from this track's prompt order: set up, run a prompt, execute it in the real world, review weekly, ship on day 30.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right"><div className="font-display text-3xl font-black text-white" data-testid="plan-progress">{pct}%</div><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{checked.size}/30 days</div></div>
          <button type="button" onClick={exportCsv} className="inline-flex min-h-10 items-center gap-2 border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-200 hover:border-purple-400" data-testid="plan-export-csv"><Download size={13} /> CSV</button>
        </div>
      </div>
      {plan.locked && <div className="flex items-center gap-2 px-6 py-3 text-xs text-zinc-400 border-b border-zinc-800" data-testid="plan-locked"><Lock size={12} /> Unlock this track to tick days off and save your progress.</div>}
      <ol className="grid gap-px bg-zinc-900 sm:grid-cols-2 lg:grid-cols-3">
        {plan.days.map((d) => {
          const done = checked.has(d.day);
          return (
            <li key={d.day} className={`flex items-start gap-3 bg-[#08070b] px-4 py-3 ${done ? "opacity-60" : ""}`} data-testid={`plan-day-${d.day}`}>
              <button type="button" onClick={() => toggle(d.day)} disabled={plan.locked} aria-label={`Mark day ${d.day} ${done ? "not done" : "done"}`}
                className="-m-3 flex h-11 w-11 shrink-0 items-center justify-center" data-testid={`plan-day-${d.day}-toggle`}>
                <span className={`flex h-5 w-5 items-center justify-center border ${done ? "border-emerald-400 bg-emerald-400 text-black" : "border-zinc-600"}`} aria-hidden="true">{done && <Check size={12} strokeWidth={3} />}</span>
              </button>
              <div className="min-w-0">
                <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${KIND[d.kind]}`}>Day {d.day} · {d.kind}</div>
                {d.kind === "prompt" && onJumpToPrompt ? (
                  <button type="button" onClick={() => onJumpToPrompt(d.prompt_index)} className={`text-left text-sm text-zinc-200 hover:text-purple-200 ${done ? "line-through" : ""}`}>{d.title}</button>
                ) : (
                  <div className={`text-sm text-zinc-300 ${done ? "line-through" : ""}`}>{d.title}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};
